import React, {useState, useRef, useEffect, useCallback} from 'react';
import {useAuth} from '@/hooks/useAuth';
import {useAdminNotifications} from '../../contexts/AdminNotificationContext';
import {Loader2, Plus, Save, X, Edit} from 'lucide-react';
import AdminService from '../../services/adminService';

// RuleType enum matching the API
export enum RuleType {
    GREATER_THAN = 'greater_than',
    LESS_THAN = 'less_than',
    RANGE = 'range',
    OUTSIDE_RANGE = 'outside_range',
    EQUALS = 'equals',
    NOT_EQUALS = 'not_equals',
}

// Interfaces matching the old component
export interface BiomarkerRule {
    test_type: string;
    rule_type: RuleType;
    min_value?: number | null;
    max_value?: number | null;
    threshold_value?: number | null;
    rule_expression?: string;
    gender?: string | null;
}

export interface Recommendation {
    Id: number;
    name: string;
    enabled: boolean;
    content?: string; // Description field
    image: string;
    sup_type: string;
    price: number;
    members_price: number;
    url: string;
    biomarkers: BiomarkerRule[];
}

// Helper functions from old component
const formatRuleTypeLabel = (ruleType: RuleType): string => {
    switch (ruleType) {
        case RuleType.GREATER_THAN:
            return 'Greater Than';
        case RuleType.LESS_THAN:
            return 'Less Than';
        case RuleType.RANGE:
            return 'Range';
        case RuleType.OUTSIDE_RANGE:
            return 'Outside Range';
        case RuleType.EQUALS:
            return 'Equals';
        case RuleType.NOT_EQUALS:
            return 'Not Equals';
        default:
            return ruleType;
    }
};

const requiresRangeInputs = (ruleType: RuleType): boolean => {
    return ruleType === RuleType.RANGE || ruleType === RuleType.OUTSIDE_RANGE;
};

export const AdminRecommendations: React.FC = () => {
    const {user} = useAuth();
    const {showSuccess, showError} = useAdminNotifications();

    const [toast, setToast] = useState({
        error: false,
        message: '',
    });

    const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [loadingEdit, setLoadingEdit] = useState(false);

    // Autocomplete state
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [activeBiomarkerIndex, setActiveBiomarkerIndex] = useState<number | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef<number | null>(null);
    const autocompleteRef = useRef<HTMLDivElement | null>(null);

    const [state, setState] = useState<Recommendation>({
        Id: 0,
        name: '',
        enabled: false,
        content: '',
        image: '',
        sup_type: '',
        price: 0,
        members_price: 0,
        url: '',
        biomarkers: [
            {
                test_type: '',
                rule_type: RuleType.GREATER_THAN,
                min_value: null,
                max_value: null,
                threshold_value: null,
                rule_expression: '',
                gender: null,
            },
        ],
    });

    // Fetch recommendations using AdminService
    const fetchRecommendations = React.useCallback(async () => {
        if (!user?.token) return;

        setLoading(true);
        try {
            const apiData = await AdminService.getAllRecommendations();
            const normalized: Recommendation[] = apiData.map((rec: any) => {
                if (Array.isArray(rec?.biomarkers)) {
                    const biomarkers: BiomarkerRule[] = (rec.biomarkers as BiomarkerRule[]).map((b) => ({
                        test_type: (b.test_type ?? '').toString(),
                        rule_type: b.rule_type as RuleType,
                        min_value: b.min_value ?? null,
                        max_value: b.max_value ?? null,
                        threshold_value: b.threshold_value ?? null,
                        rule_expression: b.rule_expression ?? '',
                        gender: b.gender ?? null,
                    }));
                    return {
                        Id: rec.Id,
                        name: rec.name,
                        enabled: rec.enabled,
                        image: rec.image || '',
                        sup_type: rec.sup_type || '',
                        price: rec.price || 0,
                        members_price: rec.members_price || 0,
                        url: rec.url || '',
                        biomarkers,
                    } as Recommendation;
                }
                // Legacy single-rule recommendation → wrap into biomarkers array
                const biomarker: BiomarkerRule = {
                    test_type: rec.test_type ?? '',
                    rule_type: rec.rule_type,
                    min_value: rec.min_value ?? null,
                    max_value: rec.max_value ?? null,
                    threshold_value: rec.threshold_value ?? null,
                    rule_expression: rec.rule_expression ?? '',
                    gender: rec.gender ?? null,
                };
                return {
                    Id: rec.Id,
                    name: rec.name,
                    enabled: rec.enabled,
                    image: rec.image || '',
                    sup_type: rec.sup_type || '',
                    price: rec.price || 0,
                    members_price: rec.members_price || 0,
                    url: rec.url || '',
                    biomarkers: [biomarker],
                } as Recommendation;
            });
            setRecommendations(normalized);
        } catch (error) {
            console.error('Failed to fetch recommendations', error);
            showError('Failed to fetch recommendations');
        } finally {
            setLoading(false);
        }
    }, [user?.token, showError]);

    React.useEffect(() => {
        fetchRecommendations();
    }, [fetchRecommendations, user?.token]);

    // Close autocomplete when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
                setSearchResults([]);
                setActiveBiomarkerIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback((searchTerm: string, biomarkerIndex: number) => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // If search term is too short, clear results
        if (searchTerm.trim().length < 2) {
            setSearchResults([]);
            setActiveBiomarkerIndex(null);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setActiveBiomarkerIndex(biomarkerIndex);

        // Set new timeout
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const results = await AdminService.searchBiomarkers(searchTerm);
                setSearchResults(results);
            } catch (error) {
                console.error('Failed to search biomarkers', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300); // 300ms debounce delay
    }, []);

    // Handle biomarker input change with search
    const handleBiomarkerChange = (index: number, value: string) => {
        setState({
            ...state,
            biomarkers: state.biomarkers.map((x, idx) => (idx === index ? {...x, test_type: value} : x)),
        });

        // Trigger debounced search
        debouncedSearch(value, index);
    };

    // Handle selection from autocomplete
    const handleSelectBiomarker = (biomarkerName: string, index: number) => {
        setState({
            ...state,
            biomarkers: state.biomarkers.map((x, idx) => (idx === index ? {...x, test_type: biomarkerName} : x)),
        });
        setSearchResults([]);
        setActiveBiomarkerIndex(null);
    };

    // Reset form to initial state
    const resetForm = () => {
        setState({
            Id: 0,
            name: '',
            enabled: false,
            content: '',
            image: '',
            sup_type: '',
            price: 0,
            members_price: 0,
            url: '',
            biomarkers: [
                {
                    test_type: '',
                    rule_type: RuleType.GREATER_THAN,
                    min_value: null,
                    max_value: null,
                    threshold_value: null,
                    rule_expression: '',
                    gender: null,
                },
            ],
        });
        setToast({error: false, message: ''});
    };

    // Validate if all required fields are filled
    const isFormValid = (): boolean => {
        if (!state.name.trim()) return false;
        if (!state.image.trim()) return false;
        if (!state.sup_type.trim()) return false;
        if (state.price <= 0) return false;
        if (state.members_price <= 0) return false;
        if (!state.url.trim()) return false;

        // Check that each biomarker has required fields
        for (const biomarker of state.biomarkers) {
            if (!biomarker.test_type.trim()) return false;

            if (requiresRangeInputs(biomarker.rule_type)) {
                if (biomarker.min_value === null || biomarker.max_value === null) return false;
            } else {
                if (biomarker.threshold_value === null) return false;
            }
        }

        return true;
    };

    // Handle form submission - exactly matching old component
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Prevent double submission
        if (submitting) return;

        setSubmitting(true);
        setToast({
            error: false,
            message: '',
        });

        // Validate form fields - exactly matching old validation
        if (!state.name.trim()) {
            setToast({
                error: true,
                message: 'Please enter a name',
            });
            setSubmitting(false);
            return;
        }

        if (state.name.length > 50) {
            setToast({
                error: true,
                message: 'Name must be 50 characters or less',
            });
            setSubmitting(false);
            return;
        }

        if (state.biomarkers.length === 0 || !state.biomarkers[0].test_type.trim()) {
            setToast({
                error: true,
                message: 'Please enter at least one biomarker and rule',
            });
            setSubmitting(false);
            return;
        }

        // Validate each biomarker based on its rule type - exactly matching old validation
        for (const b of state.biomarkers) {
            if (!b.test_type.trim()) {
                setToast({error: true, message: 'Each biomarker must have a name'});
                setSubmitting(false);
                return;
            }
            if (requiresRangeInputs(b.rule_type)) {
                const minVal = typeof b.min_value === 'number' ? b.min_value : Number.NEGATIVE_INFINITY;
                const maxVal = typeof b.max_value === 'number' ? b.max_value : Number.POSITIVE_INFINITY;
                if (!(minVal < maxVal)) {
                    setToast({error: true, message: `For ${b.test_type}, minimum must be less than maximum`});
                    setSubmitting(false);
                    return;
                }
            } else {
                if (typeof b.threshold_value !== 'number' || isNaN(b.threshold_value)) {
                    setToast({error: true, message: `For ${b.test_type}, please enter a valid value`});
                    setSubmitting(false);
                    return;
                }
            }
        }

        if (!state.image.trim()) {
            setToast({
                error: true,
                message: 'Please enter an image URL',
            });
            setSubmitting(false);
            return;
        }

        if (!state.sup_type.trim()) {
            setToast({
                error: true,
                message: 'Please enter a supplement type',
            });
            setSubmitting(false);
            return;
        }

        if (state.price <= 0) {
            setToast({
                error: true,
                message: 'Please enter a valid price greater than 0',
            });
            setSubmitting(false);
            return;
        }

        if (state.members_price <= 0) {
            setToast({
                error: true,
                message: 'Please enter a valid members price greater than 0',
            });
            setSubmitting(false);
            return;
        }

        if (!state.url.trim()) {
            setToast({
                error: true,
                message: 'Please enter a product URL',
            });
            setSubmitting(false);
            return;
        }

        // Create body exactly matching old component
        const body = {
            name: state.name,
            enabled: state.enabled,
            content: state.content,
            image: state.image,
            sup_type: state.sup_type,
            price: state.price,
            members_price: state.members_price,
            url: state.url,
            biomarkers: state.biomarkers.map((b) => ({
                test_type: b.test_type.trim(),
                rule_type: b.rule_type,
                min_value: requiresRangeInputs(b.rule_type) ? b.min_value : null,
                max_value: requiresRangeInputs(b.rule_type) ? b.max_value : null,
                threshold_value: !requiresRangeInputs(b.rule_type) ? b.threshold_value : null,
                rule_expression: b.rule_expression ?? '',
                gender: b.gender ?? null,
            })),
        };

        try {
            if (editingId) {
                // Update existing recommendation
                await AdminService.updateRecommendation(editingId, body);
                showSuccess('Successfully updated recommendation');
            } else {
                // Create new recommendation
                await AdminService.addRecommendation(body);
                showSuccess('Successfully saved recommendation');
            }

            setToast({
                error: false,
                message: editingId ? 'Successfully updated recommendation' : 'Successfully saved recommendation',
            });

            // Reset form and close modal
            resetForm();
            setEditingId(null);
            setShowModal(false);

            // Refresh recommendations list
            await fetchRecommendations();
        } catch {
            const errorMessage = editingId
                ? 'Error: Could not update recommendation'
                : 'Error: Could not save recommendation';
            setToast({
                error: true,
                message: errorMessage,
            });
            showError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle edit - fetch recommendation and populate form
    const handleEdit = async (id: number) => {
        if (!user?.token || loadingEdit) return;

        setLoadingEdit(true);
        setToast({error: false, message: ''});

        try {
            const recommendation = await AdminService.getRecommendation(id);

            // Populate the form with the fetched data
            setState({
                Id: recommendation.Id,
                name: recommendation.name || '',
                enabled: recommendation.enabled || false,
                content: recommendation.content || '',
                image: recommendation.image || '',
                sup_type: recommendation.sup_type || '',
                price: recommendation.price || 0,
                members_price: recommendation.members_price || 0,
                url: recommendation.url || '',
                biomarkers: recommendation.biomarkers || [],
            });

            // Set editing mode and show modal
            setEditingId(id);
            setShowModal(true);
        } catch (error) {
            console.error('Failed to fetch recommendation', error);
            showError('Failed to fetch recommendation for editing');
        } finally {
            setLoadingEdit(false);
        }
    };

    // Handle delete using AdminService
    const handleDelete = async (id: number) => {
        if (!user?.token || deletingIds.has(id)) return;

        setDeletingIds((prev) => new Set(prev).add(id));

        try {
            await AdminService.deleteRecommendation(id);
            await fetchRecommendations();
            showSuccess('Recommendation deleted successfully');
        } catch (error) {
            console.error('Failed to delete recommendation', error);
            showError('Failed to delete recommendation');
        } finally {
            setDeletingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
    };

    return (
        <div className="space-y-6">
            {/* Add New Recommendation Button */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-mld-text-primary">Recommendations</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setEditingId(null);
                        setShowModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90">
                    <Plus className="w-4 h-4" />
                    <span>Add New Recommendation</span>
                </button>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            {/* Modal Header */}
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-mld-text-primary">
                                    {editingId ? 'Edit Recommendation' : 'Add a Recommendation'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingId(null);
                                        resetForm();
                                    }}
                                    className="text-gray-500 hover:text-gray-700">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Modal Form */}
                            <form onSubmit={handleSubmit}>
                                {/* Name Field */}
                                <div className="mb-6">
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Name: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={state.name}
                                        onChange={(e) => setState({...state, name: e.target.value})}
                                        maxLength={50}
                                        placeholder="Enter a name (max 50 characters)"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Description Field */}
                                <div className="mb-6">
                                    <label
                                        htmlFor="content"
                                        className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Description:
                                    </label>
                                    <textarea
                                        id="content"
                                        value={state.content || ''}
                                        onChange={(e) => setState({...state, content: e.target.value})}
                                        maxLength={200}
                                        placeholder="Enter a short description (max 200 characters)"
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent resize-none"
                                    />
                                </div>

                                {/* Image URL Field */}
                                <div className="mb-6">
                                    <label
                                        htmlFor="image"
                                        className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Image URL: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        id="image"
                                        value={state.image}
                                        onChange={(e) => setState({...state, image: e.target.value})}
                                        placeholder="Enter image URL"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Supplement Type Field */}
                                <div className="mb-6">
                                    <label
                                        htmlFor="sup_type"
                                        className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Supplement Type: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="sup_type"
                                        value={state.sup_type}
                                        onChange={(e) => setState({...state, sup_type: e.target.value})}
                                        placeholder="Enter supplement type"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Price Fields */}
                                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label
                                            htmlFor="price"
                                            className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Price ($): <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="price"
                                            value={state.price}
                                            onChange={(e) =>
                                                setState({...state, price: parseFloat(e.target.value) || 0})
                                            }
                                            min="0.01"
                                            step="0.01"
                                            placeholder="Enter price"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="members_price"
                                            className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Members Price ($): <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="members_price"
                                            value={state.members_price}
                                            onChange={(e) =>
                                                setState({...state, members_price: parseFloat(e.target.value) || 0})
                                            }
                                            min="0.01"
                                            step="0.01"
                                            placeholder="Enter members price"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* URL Field */}
                                <div className="mb-6">
                                    <label
                                        htmlFor="url"
                                        className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Product URL: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        id="url"
                                        value={state.url}
                                        onChange={(e) => setState({...state, url: e.target.value})}
                                        placeholder="Enter product URL"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Biomarker Rules */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-mld-text-primary mb-3">
                                        Biomarker Rules:
                                    </label>
                                    {state.biomarkers.map((b, i) => (
                                        <div key={i} className="border border-gray-200 p-3 rounded-md mb-3">
                                            <div className="flex gap-3 flex-wrap items-end">
                                                <div className="flex-1 min-w-[220px] relative">
                                                    <label
                                                        htmlFor={`biomarker-${i}`}
                                                        className="block text-sm font-medium text-gray-700 mb-1">
                                                        Biomarker
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id={`biomarker-${i}`}
                                                        value={b.test_type}
                                                        onChange={(e) => handleBiomarkerChange(i, e.target.value)}
                                                        onBlur={(e) => {
                                                            // Trim value on blur
                                                            setState({
                                                                ...state,
                                                                biomarkers: state.biomarkers.map((x, idx) =>
                                                                    idx === i
                                                                        ? {...x, test_type: e.target.value.trim()}
                                                                        : x
                                                                ),
                                                            });
                                                            // Close autocomplete after a short delay to allow clicks
                                                            setTimeout(() => {
                                                                if (activeBiomarkerIndex === i) {
                                                                    setSearchResults([]);
                                                                    setActiveBiomarkerIndex(null);
                                                                }
                                                            }, 200);
                                                        }}
                                                        placeholder="e.g., LDL"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                                        autoComplete="off"
                                                    />
                                                    {/* Autocomplete dropdown */}
                                                    {activeBiomarkerIndex === i &&
                                                        (isSearching || searchResults.length > 0) && (
                                                            <div
                                                                ref={autocompleteRef}
                                                                className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                                                {isSearching ? (
                                                                    <div className="px-3 py-2 text-sm text-gray-500 flex items-center">
                                                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                                        Searching...
                                                                    </div>
                                                                ) : searchResults.length === 0 ? (
                                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                                        No results found
                                                                    </div>
                                                                ) : (
                                                                    searchResults.map((result, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            className="px-3 py-2 text-sm hover:bg-mld-primary hover:text-white cursor-pointer transition-colors"
                                                                            onMouseDown={(e) => {
                                                                                e.preventDefault(); // Prevent blur event
                                                                                handleSelectBiomarker(result, i);
                                                                            }}>
                                                                            {result}
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                </div>
                                                <div className="flex-1 min-w-[220px]">
                                                    <label
                                                        htmlFor={`ruleType-${i}`}
                                                        className="block text-sm font-medium text-gray-700 mb-1">
                                                        Rule Type
                                                    </label>
                                                    <select
                                                        id={`ruleType-${i}`}
                                                        value={b.rule_type}
                                                        onChange={(e) =>
                                                            setState({
                                                                ...state,
                                                                biomarkers: state.biomarkers.map((x, idx) =>
                                                                    idx === i
                                                                        ? {...x, rule_type: e.target.value as RuleType}
                                                                        : x
                                                                ),
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                                        {Object.values(RuleType).map((ruleType) => (
                                                            <option key={ruleType} value={ruleType}>
                                                                {formatRuleTypeLabel(ruleType)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex-1 min-w-[160px]">
                                                    <label
                                                        htmlFor={`gender-${i}`}
                                                        className="block text-sm font-medium text-gray-700 mb-1">
                                                        Gender (Optional)
                                                    </label>
                                                    <select
                                                        id={`gender-${i}`}
                                                        value={b.gender ?? ''}
                                                        onChange={(e) =>
                                                            setState({
                                                                ...state,
                                                                biomarkers: state.biomarkers.map((x, idx) =>
                                                                    idx === i
                                                                        ? {...x, gender: e.target.value || null}
                                                                        : x
                                                                ),
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                                        <option value="">All Genders</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                    </select>
                                                </div>
                                                {requiresRangeInputs(b.rule_type) ? (
                                                    <>
                                                        <div className="flex-1 min-w-[160px]">
                                                            <label
                                                                htmlFor={`min-${i}`}
                                                                className="block text-sm font-medium text-gray-700 mb-1">
                                                                Minimum
                                                            </label>
                                                            <input
                                                                type="number"
                                                                id={`min-${i}`}
                                                                value={b.min_value ?? ''}
                                                                onChange={(e) =>
                                                                    setState({
                                                                        ...state,
                                                                        biomarkers: state.biomarkers.map((x, idx) =>
                                                                            idx === i
                                                                                ? {
                                                                                      ...x,
                                                                                      min_value:
                                                                                          e.target.value === ''
                                                                                              ? null
                                                                                              : parseFloat(
                                                                                                    e.target.value
                                                                                                ),
                                                                                  }
                                                                                : x
                                                                        ),
                                                                    })
                                                                }
                                                                step="0.01"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-[160px]">
                                                            <label
                                                                htmlFor={`max-${i}`}
                                                                className="block text-sm font-medium text-gray-700 mb-1">
                                                                Maximum
                                                            </label>
                                                            <input
                                                                type="number"
                                                                id={`max-${i}`}
                                                                value={b.max_value ?? ''}
                                                                onChange={(e) =>
                                                                    setState({
                                                                        ...state,
                                                                        biomarkers: state.biomarkers.map((x, idx) =>
                                                                            idx === i
                                                                                ? {
                                                                                      ...x,
                                                                                      max_value:
                                                                                          e.target.value === ''
                                                                                              ? null
                                                                                              : parseFloat(
                                                                                                    e.target.value
                                                                                                ),
                                                                                  }
                                                                                : x
                                                                        ),
                                                                    })
                                                                }
                                                                step="0.01"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="flex-1 min-w-[200px]">
                                                        <label
                                                            htmlFor={`threshold-${i}`}
                                                            className="block text-sm font-medium text-gray-700 mb-1">
                                                            Value
                                                        </label>
                                                        <input
                                                            type="number"
                                                            id={`threshold-${i}`}
                                                            value={b.threshold_value ?? ''}
                                                            onChange={(e) =>
                                                                setState({
                                                                    ...state,
                                                                    biomarkers: state.biomarkers.map((x, idx) =>
                                                                        idx === i
                                                                            ? {
                                                                                  ...x,
                                                                                  threshold_value:
                                                                                      e.target.value === ''
                                                                                          ? null
                                                                                          : parseFloat(e.target.value),
                                                                              }
                                                                            : x
                                                                    ),
                                                                })
                                                            }
                                                            step="0.01"
                                                            placeholder="Enter value"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                                        />
                                                    </div>
                                                )}
                                                {state.biomarkers.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setState({
                                                                ...state,
                                                                biomarkers: state.biomarkers.filter(
                                                                    (_, idx) => idx !== i
                                                                ),
                                                            })
                                                        }
                                                        className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setState({
                                                ...state,
                                                biomarkers: [
                                                    ...state.biomarkers,
                                                    {
                                                        test_type: '',
                                                        rule_type: RuleType.GREATER_THAN,
                                                        min_value: null,
                                                        max_value: null,
                                                        threshold_value: null,
                                                        rule_expression: '',
                                                        gender: null,
                                                    },
                                                ],
                                            })
                                        }
                                        className="px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90">
                                        Add Biomarker Rule
                                    </button>
                                </div>

                                {/* Enabled Checkbox */}
                                <div className="mb-6">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={state.enabled}
                                            onChange={(e) => setState({...state, enabled: e.target.checked})}
                                            className="h-4 w-4 text-mld-primary focus:ring-mld-primary border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-mld-text-primary">Enabled</span>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <div className="flex items-center justify-between pt-6">
                                    <div className="flex items-center space-x-4">
                                        <button
                                            type="submit"
                                            disabled={submitting || loading || loadingEdit || !isFormValid()}
                                            className="flex items-center space-x-2 px-6 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {submitting ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Save className="w-4 h-4" />
                                            )}
                                            <span>{submitting ? 'Saving...' : editingId ? 'Update' : 'Save'}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowModal(false);
                                                setEditingId(null);
                                                resetForm();
                                            }}
                                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                                            Cancel
                                        </button>
                                    </div>
                                    {toast.message && (
                                        <span
                                            className={`text-sm`}
                                            style={{color: toast.error ? '#F15E87' : '#42D4B0'}}>
                                            {toast.message}
                                        </span>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Existing Recommendations */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-mld-text-primary mb-6">Existing Recommendations</h2>
                {loading ? (
                    <p className="text-gray-500">Loading recommendations...</p>
                ) : recommendations.length === 0 ? (
                    <p className="text-gray-500">No recommendations found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-2 text-left border-b border-gray-300 font-medium">Name</th>
                                    <th className="p-2 text-left border-b border-gray-300 font-medium">
                                        Biomarker Rules
                                    </th>
                                    <th className="p-2 text-left border-b border-gray-300 font-medium">Enabled</th>
                                    <th className="p-2 text-left border-b border-gray-300 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recommendations.map((recommendation) => (
                                    <tr key={recommendation.Id}>
                                        <td className="p-2 border-b border-gray-100 align-top">
                                            {recommendation.name}
                                        </td>
                                        <td className="p-2 border-b border-gray-100">
                                            <ul className="list-disc list-inside">
                                                {recommendation.biomarkers.map((b, idx) => (
                                                    <li key={idx} className="mb-1 text-sm">
                                                        <span className="font-medium">{b.test_type}</span>:{' '}
                                                        {formatRuleTypeLabel(b.rule_type)}{' '}
                                                        {requiresRangeInputs(b.rule_type)
                                                            ? `${b.min_value ?? ''} - ${b.max_value ?? ''}`
                                                            : `${b.threshold_value ?? ''}`}
                                                        {b.gender && (
                                                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                                                {b.gender.charAt(0).toUpperCase() + b.gender.slice(1)}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </td>
                                        <td className="p-2 border-b border-gray-100 align-top">
                                            <span
                                                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                                                style={{
                                                    backgroundColor: recommendation.enabled ? '#42D4B0' : '#F15E87',
                                                }}>
                                                {recommendation.enabled ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="p-2 border-b border-gray-100 align-top">
                                            <div className="flex space-x-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEdit(recommendation.Id)}
                                                    disabled={loadingEdit}
                                                    className="flex items-center space-x-1 px-2 py-1 bg-mld-primary text-white text-sm rounded hover:bg-mld-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                                                    {loadingEdit ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <Edit className="w-3 h-3" />
                                                    )}
                                                    <span>{loadingEdit ? 'Loading...' : 'Edit'}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(recommendation.Id)}
                                                    disabled={deletingIds.has(recommendation.Id)}
                                                    className="flex items-center space-x-1 px-2 py-1 text-white text-sm rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    style={{backgroundColor: '#F15E87'}}>
                                                    {deletingIds.has(recommendation.Id) && (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    )}
                                                    <span>
                                                        {deletingIds.has(recommendation.Id) ? 'Deleting...' : 'Delete'}
                                                    </span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
